/* eslint-disable no-nested-ternary */
import * as React from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandItem,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ChevronDown, X } from 'lucide-react';
import { CommandGroup } from 'cmdk';

export interface MultiSelectOption {
  value: string;
  label: string;
  avatarUrl?: string;
}

interface SingleSelectProps {
  /** Button label when nothing is selected */
  placeholder: string;
  /** Optional icon on the left side of the button */
  icon?: React.ReactNode;

  /** Full flat list of options */
  options: MultiSelectOption[];

  /** Controlled selected value (or undefined for none) */
  value?: string;
  /** Callback whenever the selection changes */
  onChange: (v?: string) => void;

  /** Show search field inside the popover (default = false) */
  searchable?: boolean;
  /** Force-show avatar column (default = true if any option has `avatarUrl`) */
  showAvatar?: boolean;

  /** Extra Tailwind classes for the trigger button */
  className?: string;

  onSearch?: (search: string) => void;

  isLoading?: boolean;
}

export function SingleSelect({
  placeholder,
  icon,
  options,
  value,
  onChange,
  searchable = false,
  showAvatar,
  className,
  onSearch,
  isLoading,
}: SingleSelectProps) {
  const [selectedOption, setSelectedOption] = React.useState<MultiSelectOption | null>(
    () => options.find(o => o.value === value) ?? null
  );
  const [searchTerm, setSearchTerm] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  // whenever the user types, keep local state + call onSearch
  const handleSearch = (val: string) => {
    setSearchTerm(val);
    onSearch?.(val);
  };

  const handleSelect = (opt: MultiSelectOption) => {
    setSelectedOption(opt);
    onChange(opt.value);
  };

  const displayLabel = selectedOption?.label ?? placeholder;

  const autoShowAvatar =
    showAvatar ?? options.some(o => o.avatarUrl !== undefined);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, [options]);

  React.useEffect(()=>{
    onSearch('');
  },[])

  return (
    <Popover>
      {/* ───── Trigger button ───── */}
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`gap-2 max-w-xs text-muted ${className ?? ''}`}
        >
          {icon}
          {value ? (
            <>
              <Badge
                variant="secondary"
                className="gap-1 pl-2 pr-1 max-w-[220px] text-muted  cursor-pointer"
              >

              <p className='max-w-[154px] truncate'>{displayLabel}</p>
              {selectedOption && (
                <X 
                  className="size-3 ml-1" 
                  onClick={e => {
                      e.stopPropagation();
                      onChange(undefined);
                    }} 
                />
                )}
              </Badge>
            </>
          ) : (
            placeholder
          )}
          <ChevronDown size={16} color="#71717A" />
        </Button>
      </PopoverTrigger>

      {/* ───── Popover panel ───── */}
      <PopoverContent className="w-[380px] p-0">
        <Command loop key={options.map(o => o.value).join(',')}>
          {searchable && (
            <CommandInput
              ref={inputRef}
              placeholder="Search here"
              value={searchTerm}
              className="h-9 px-2 py-1 my-1 ring-0 outline-none border-0 focus:outline-none focus:ring-0 focus:border-current"
              onValueChange={handleSearch}
            />
          )}

          <CommandList className="max-h-60 [&::-webkit-scrollbar-thumb]:bg-[#09090B]">
            {isLoading && <CommandEmpty>Searching...</CommandEmpty>}

            {!isLoading && searchTerm && options.length === 0 && (
              <CommandEmpty>No result</CommandEmpty>
            )}

            {options?.length > 0 && (
              <CommandGroup key={options.length} className="p-1">
                {options?.map(opt => {
                  const selected = value === opt.value;
                  return (
                    <CommandItem
                      key={opt.value}
                      onSelect={() =>
                        handleSelect(opt)
                      }
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer"
                    >
                      <Checkbox checked={selected} />

                      {autoShowAvatar && (
                        <Avatar className="h-8 w-8">
                          {opt.avatarUrl ? (
                            <AvatarImage src={opt.avatarUrl} alt={opt.label} />
                          ) : (
                            <AvatarFallback>
                              {opt.label
                                .split(' ')
                                .map(s => s[0])
                                .join('')
                                .slice(0, 2)
                                .toUpperCase()}
                            </AvatarFallback>
                          )}
                        </Avatar>
                      )}

                      <span className="truncate">{opt.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
